import type { T_Maybe } from '@tryiris-ai/js-types';
import { Tool } from './types.js';

/** A per-tool availability entry — mirrors the top-tray's computed gate ({@link Tool} + disabled). */
export type ToolAvailability = { disabled?: T_Maybe<boolean>; tool: Tool };

/**
 * ENG-4495 D5 / AC4 — resolve the EFFECTIVE selected tool on a generic (driverless) device.
 *
 * The tool preference (`useToolPref` / `PrefKeys.tool`) is a GLOBAL user setting whose default is
 * `Tool.Center`. On a generic model the driver-dependent magic tools — Center / FastFrame / Tracking —
 * are PERMANENTLY unavailable (C4/D4), so a persisted magic-tool selection would (a) highlight a dead
 * top-tray button and (b) leave click/drag inert (the interaction handler is gated off for them).
 * Derive an effective selection — WITHOUT writing the pref, so returning to a branded camera restores
 * the user's real choice — that falls back to the first usable manual tool: prefer Joystick (so
 * click/drag moves the camera), else Zoom, else the first non-disabled tool.
 *
 * D3 tri-state: scoped to `bGeneric === true` ONLY. A NON-generic device is returned UNCHANGED (AC4);
 * an uncalibrated branded magic tool is disabled today too, but it becomes usable once calibrated, so
 * keeping it selected is correct — we must NOT remap it. `available` is optional: the interaction
 * handler carries no per-tool gate list and only needs the Joystick backstop, so it omits it.
 *
 * @param tool - The persisted (global) tool selection.
 * @param bGeneric - Whether the selected device is a LOADED generic model (never `?? false`).
 * @param available - Optional per-tool disabled map (the top-tray's computed gate).
 * @returns The effective tool to highlight / activate.
 */
export const toEffectiveTool = (
  tool: Tool,
  bGeneric: boolean,
  available?: readonly ToolAvailability[]
): Tool => {
  // D5/AC4: only a generic device's PERMANENTLY-blocked magic tool is remapped; everything else
  // (branded devices, and the always-usable manual tools) is returned exactly as selected.
  if (!bGeneric || ![Tool.Center, Tool.FastFrame, Tool.Tracking].includes(tool)) {
    return tool;
  }

  // No availability map (interaction handler) ⇒ treat manual tools as usable — Joystick is the
  // backstop that makes click/drag move the camera.
  const usable = (candidate: Tool): boolean =>
    !available || available.some((µ) => µ.tool === candidate && !µ.disabled);

  if (usable(Tool.Joystick)) {
    return Tool.Joystick;
  }

  if (usable(Tool.Zoom)) {
    return Tool.Zoom;
  }

  // Degenerate: neither manual tool is usable — fall back to the first non-disabled tool, or keep
  // Joystick as the manual default when the caller supplied no availability map.
  return available?.find((µ) => !µ.disabled)?.tool ?? Tool.Joystick;
};
